'use client';

import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, usePathname } from 'next/navigation';
import { RootState } from '@/hotdesk-ui/store/store';
import { setIsOpenAuthModal } from '@/hotdesk-ui/store/slices/auth.slice';
import { RemoteConfigKeys } from '@/hotdesk-ui/enum/remote-keys';
import { fetchRemoteConfig } from '@/hotdesk-ui/common-classes/firebase.service';
import { isTokenExpired } from '@/hotdesk-ui/utils/tokenUtils';
import AuthModalV4 from './AuthModalV4';
interface AuthClientV4Props {
  language: string;
  envUrl: string;
}
const AuthClientV4: FC<AuthClientV4Props> = ({
  language,
  envUrl
}) => {
  const isAuthOpen = useSelector((state: RootState) => state.auth.isOpenAuthModal);
  const token = useSelector((state: RootState) => state.auth.token);
  const initialStep = useSelector((state: RootState) => state.auth.authModalInitialStep);
  const initialEmail = useSelector((state: RootState) => state.auth.authModalInitialEmail);
  const dispatch = useDispatch();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [termLink, setTermLink] = useState('');
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState('');
  const [googleLogin, setGoogleLogin] = useState(true);
  const [appleLogin, setAppleLogin] = useState(false);
  const [itidaTextFieldEnabled, setItidaTextFieldEnabled] = useState(false);
  const isItidaInUrl = searchParams?.get('itida') === 'true' || false;
  const isItida = isItidaInUrl && itidaTextFieldEnabled;
  const isAuthenticated = !!token && !isTokenExpired(token);
  const isMyAccountPage = pathname === '/my-account';
  useEffect(() => {
    const loadRemoteConfig = async () => {
      try {
        const termLinkText = (await fetchRemoteConfig(RemoteConfigKeys.TERMS_AND_CONDITIONS_KEY, 'object')) as Record<string, string>;
        setTermLink(termLinkText?.[language] ?? '');
        const privacyLink = (await fetchRemoteConfig(RemoteConfigKeys.PRIVACY_KEY, 'object')) as Record<string, string>;
        setPrivacyPolicyLink(privacyLink?.[language] ?? '');
        const googleEnabled = (await fetchRemoteConfig(RemoteConfigKeys.GOOGLE_LOGIN_KEY, 'boolean')) as boolean;
        setGoogleLogin(googleEnabled);
        const appleEnabled = (await fetchRemoteConfig(RemoteConfigKeys.APPLE_LOGIN_KEY, 'boolean')) as boolean;
        setAppleLogin(appleEnabled);
        const itidaTextField = (await fetchRemoteConfig(RemoteConfigKeys.WEB_LINKED_ACCOUNTS_ITIDA_TEXT_FIELD, 'boolean')) as boolean;
        const selfUrl = process.env.NEXT_PUBLIC_SELF_URL;
        if (selfUrl === 'https://www.hotdesk.com/') {
          setItidaTextFieldEnabled(itidaTextField);
        } else {
          setItidaTextFieldEnabled(true);
        }
      } catch (error) {
        console.error(error);
      }
    };
    loadRemoteConfig();
  }, [language]);
  useEffect(() => {
    if (isItidaInUrl && itidaTextFieldEnabled && !isAuthenticated && !isMyAccountPage) {
      dispatch(setIsOpenAuthModal(true));
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('itida_redirect', 'true');
      }
    }
  }, [isItidaInUrl, itidaTextFieldEnabled, isAuthenticated, isMyAccountPage, dispatch]);
  return <AuthModalV4 show={isAuthOpen} language={language} termLink={termLink} privacyPolicyLink={privacyPolicyLink} handleClose={() => dispatch(setIsOpenAuthModal(false))} envUrl={envUrl} googleLogin={googleLogin} appleLogin={appleLogin} isItida={isItida} initialStep={initialStep} initialEmail={initialEmail} data-sentry-element="AuthModalV4" data-sentry-component="AuthClientV4" data-sentry-source-file="AuthClientV4.tsx" />;
};
export default AuthClientV4;
export { AuthClientV4 };